import React from 'react';
import { useSelfHostedContext } from './SelfHostedModalContextProvider';
import {
  Box,
  CustomModalLayout,
  type CustomModalLayoutProps,
  Stepper,
} from '@wix/design-system';
import classNames from 'classnames';
import css from './SelfHostedModal.scss';
import { DataHooks } from '../../../../constants/dataHooks';
import type { SelfHostedModalBiParams } from './types';
import { useTemplatesData } from '../../../../hooks/useTemplatesData';
import { useTemplatesPageBi } from '../../../../hooks/useTemplatesPageBi';

export const SelfHostedModalLayout = ({
  children,
  className,
  biParams,
  primaryButtonOnClick,
  secondaryButtonOnClick,
  ...rest
}: Partial<CustomModalLayoutProps> & { biParams: SelfHostedModalBiParams }) => {
  const { activeStep, steps, onClose, previousStep } = useSelfHostedContext();
  const { currentTemplate: template } = useTemplatesData();
  const {
    formData: { appDetails },
  } = useSelfHostedContext();

  const {
    reportCloseButtonClick,
    reportMainCTAClick,
    reportSecondaryCTAClick,
  } = useTemplatesPageBi();

  const onStepClick =
    activeStep === 1
      ? (clickedStep: number) => {
          if (clickedStep === 0) {
            previousStep();
          }
        }
      : undefined;

  const onPrimaryButtonClick = () => {
    reportMainCTAClick({
      biParams,
      appId: appDetails?.appId,
      templateId: template.id,
    });
    primaryButtonOnClick();
  };

  const onSecondaryButtonClick = () => {
    reportSecondaryCTAClick({
      biParams,
      appId: appDetails?.appId,
      templateId: template.id,
    });
    secondaryButtonOnClick();
  };

  const onCloseButtonClick = () => {
    reportCloseButtonClick({
      biParams,
      appId: appDetails?.appId,
      templateId: template.id,
    });
    onClose();
  };

  return (
    <CustomModalLayout
      width="780px"
      height="588px"
      className={classNames(css.selfHostedModal, className)}
      dataHook={DataHooks.SelfHostedModalLayout}
      showHeaderDivider
      showFooterDivider
      removeContentPadding={true}
      onCloseButtonClick={onCloseButtonClick}
      primaryButtonOnClick={onPrimaryButtonClick}
      {...(secondaryButtonOnClick && {
        secondaryButtonOnClick: onSecondaryButtonClick,
      })}
      subtitle={
        <Box className={css.stepper}>
          <Stepper
            activeStep={activeStep}
            dataHook={DataHooks.SelfHostedModalStepper}
            steps={steps}
            onClick={onStepClick}
          />
        </Box>
      }
      {...rest}
    >
      <Box className={css.modalContent} direction="vertical">
        {children}
      </Box>
    </CustomModalLayout>
  );
};
